/* @flow */

import * as React from 'react';
import classNames from 'classnames';

import styles from './GroupAvatarQueue.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import { small } from './Avatar';

import GroupAvatarQueueEmpty from './GroupAvatarQueueEmpty';
import GroupAvatarQueueAvatar from './GroupAvatarQueueAvatar';

import type { User } from '@braindate/domain/lib/user/type';

const MAX_SHOWN_CIRCLE = 5;

type Props = {
  spots?: number,
  participants: Array<User>,
  fadedAvatarsNumber?: number,
  variant?: string,
  layoutClass?: string,
};

const useStyles = createUseThemeStyles(styles);

const GroupAvatarQueue = ({
  spots = 0,
  participants,
  fadedAvatarsNumber = 0,
  variant = small,
  layoutClass,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes = useStyles({ variant });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  // SPECS = https://e180.atlassian.net/browse/PAX-1376
  const isFull = spots < 1;
  const isLengthExceedsMax = participants.length + spots > MAX_SHOWN_CIRCLE;
  const firstSectionNumber = isLengthExceedsMax
    ? MAX_SHOWN_CIRCLE - 1
    : participants.length + spots;

  const avatarsElts = participants
    .slice(0, firstSectionNumber)
    .map((participant, idx) => (
      <GroupAvatarQueueAvatar
        participant={participant}
        isFaded={idx < fadedAvatarsNumber}
        variant={variant}
        key={idx}
      />
    ));

  const emptyCirclesNumber = Math.max(
    firstSectionNumber - avatarsElts.length,
    0,
  );

  const lastElt = () => {
    if (isLengthExceedsMax) {
      const emptySpotsSuppNumber =
        spots - (firstSectionNumber - avatarsElts.length);

      return (
        <GroupAvatarQueueEmpty
          number={
            isFull
              ? participants.length - firstSectionNumber
              : spots === emptySpotsSuppNumber
              ? emptySpotsSuppNumber
              : null
          }
          hasPlus={emptySpotsSuppNumber > 1}
          variant={variant}
          isFilled={isFull}
        />
      );
    }
  };

  const emnptyCirclesElt = Array(emptyCirclesNumber)
    .fill()
    .slice(0, spots)
    .map((i, key) => <GroupAvatarQueueEmpty variant={variant} key={key} />);

  const firstSectionElts = (
    <>
      {avatarsElts} {emnptyCirclesElt}
    </>
  );
  return (
    <span className={classNames(classes.avatars, layoutClass)}>
      {firstSectionElts} {lastElt()}
    </span>
  );
};

export default React.memo<Props>(GroupAvatarQueue);
